import React, { useState } from 'react';
import './ControlPanel.css';

const ControlPanel = ({ 
  config, 
  onConfigChange, 
  lightPoints, 
  onClearAll,
  selectedPoint,
  onUpdateGroupDescription,
  onUpdateGroupConfig,
  onUpdateInstanceConfig
}) => {
  const [isPointsOpen, setIsPointsOpen] = useState(true);
  const [isLedOpen, setIsLedOpen] = useState(false);

  /** Helpers simples */
  const handleInputChange = (field, value) => {
    onConfigChange({
      ...config,
      [field]: value
    });
  };

  const handleModuleTypeChange = (moduleType) => {
    const maxIdx = moduleType === '4' ? 4 : 8;

    onConfigChange({
      ...config,
      moduleType,
      maxIdx,
      idx: parseInt(config.idx, 10) > maxIdx ? '1' : config.idx
    });
  };

  const updateDefaultsCircleNumber = (newNumber) => {
    const n = Math.max(1, parseInt(newNumber, 10) || 1);
    const moduleTypeNum = parseInt(config.moduleType || '4', 10);
    const slotIndex = n - 1;
    const newModulo = Math.floor(slotIndex / moduleTypeNum) + 1;
    const newIdx = (slotIndex % moduleTypeNum) + 1;

    onConfigChange({
      ...config,
      circleNumber: n,
      modulo: String(newModulo),
      idx: String(newIdx)
    });
  };

  /** Valores atuais */
  const currentCircleNumber = selectedPoint?.circleNumber ?? config.circleNumber;
  const currentWidth = selectedPoint?.width ?? config.width;
  const currentHeight = selectedPoint?.height ?? config.height;
  const currentX = selectedPoint?.x ?? '';
  const currentY = selectedPoint?.y ?? '';

  const circuitoDescricao = selectedPoint?.descricao ?? '';

  const moduleInfo = {
    maxIdx: parseInt(config.moduleType || '4', 10)
  };

  /** Estatísticas */
  const getPointStats = () => {
    let totalLightInstances = 0;
    let totalLedInstances = 0;
    const groupsSet = new Set();

    lightPoints.forEach(group => {
      const instances = group.instances || [];
      if (instances.length > 0) groupsSet.add(group.id);

      instances.forEach(inst => {
        if (inst.tipo === 'light') totalLightInstances += 1;
        else if (inst.tipo === 'led') totalLedInstances += 1;
      });
    });

    return {
      totalLightInstances,
      totalLedInstances,
      uniqueGroups: groupsSet.size
    };
  };

  const stats = getPointStats();

  return (
    <div className="control-panel">
      {/* Estatísticas simples */}
      <div className="stats-section">
        <div className="stat-card">
          <div className="stat-value">{stats.totalLightInstances}</div>
          <div className="stat-label">Pontos de Luz</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.totalLedInstances}</div>
          <div className="stat-label">Fitas LED</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.uniqueGroups}</div>
          <div className="stat-label">Grupos</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{config.circleNumber}</div>
          <div className="stat-label">Grupo Atual</div>
        </div>
      </div>

      {/* Accordions principais */}
      <div className="accordion">
        {/* ===== PONTOS DE LUZ ===== */}
        <div className="accordion-item">
          <button
            type="button"
            className="accordion-header"
            onClick={() => setIsPointsOpen(open => !open)}
          >
            <div className="accordion-header-main">
              <span>💡 Pontos de Luz</span>
              {config.lightType === 'normal' && (
                <span className="accordion-badge">Ferramenta ativa</span>
              )}
            </div>
            <span className="accordion-icon">
              {isPointsOpen ? '▾' : '▸'}
            </span>
          </button>

          {isPointsOpen && (
            <div className="accordion-body">
              <div className="points-config">
                <h4>Configurações dos pontos de luz</h4>

                <div className="panel-grid">
                  {/* ========== CARD 1: PRÓXIMOS PONTOS (PADRÃO) ========== */}
                  <section className="panel-card panel-card--defaults">
                    <div className="panel-card-header">
                      <span className="panel-card-title">Próximos pontos</span>
                      <span className="panel-chip">Padrão</span>
                    </div>

                    <div className="form-group">
                      <label>Tipo de Módulo</label>
                      <div className="module-selector">
                        <button 
                          type="button"
                          className={`module-btn ${config.moduleType === '4' ? 'active' : ''}`}
                          onClick={() => handleModuleTypeChange('4')}
                        >
                          4 saídas
                        </button>
                        <button 
                          type="button"
                          className={`module-btn ${config.moduleType === '8' ? 'active' : ''}`}
                          onClick={() => handleModuleTypeChange('8')}
                        >
                          8 saídas
                        </button>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="widthDefault">Largura (%)</label>
                        <input
                          type="number"
                          id="widthDefault"
                          value={config.width}
                          onChange={(e) =>
                            handleInputChange('width', parseFloat(e.target.value) || 0.1)
                          }
                          step="0.1"
                          min="0.1"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="heightDefault">Altura (%)</label>
                        <input
                          type="number"
                          id="heightDefault"
                          value={config.height}
                          onChange={(e) =>
                            handleInputChange('height', parseFloat(e.target.value) || 0.1)
                          }
                          step="0.1"
                          min="0.1"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="defaultCircleNumber">Grupo padrão</label>
                      <div className="number-controls">
                        <button
                          type="button"
                          className="number-btn decrease"
                          onClick={() => updateDefaultsCircleNumber((config.circleNumber || 1) - 1)}
                          disabled={config.circleNumber <= 1}
                        >
                          −
                        </button>
                        <input
                          type="number"
                          id="defaultCircleNumber"
                          value={config.circleNumber}
                          onChange={(e) => updateDefaultsCircleNumber(e.target.value)}
                          min="1"
                        />
                        <button
                          type="button"
                          className="number-btn increase"
                          onClick={() => updateDefaultsCircleNumber((config.circleNumber || 1) + 1)}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </section>

                  {/* ========== CARD 2: CIRCUITO (GRUPO) ========== */}
                  <section className="panel-card panel-card--group">
                    <div className="panel-card-header">
                      <span className="panel-card-title">Circuito</span>
                      <span className="panel-chip">Grupo</span>
                    </div>

                    {selectedPoint ? (
                      <>
                        <div className="form-group">
                          <label htmlFor="descricao">Nome do circuito</label>
                          <input
                            type="text"
                            id="descricao"
                            value={circuitoDescricao}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (selectedPoint?.groupId && onUpdateGroupDescription) {
                                onUpdateGroupDescription(selectedPoint.groupId, value);
                              }
                            }}
                            placeholder="Ex: Circuito 01"
                          />
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor="modulo">Módulo</label>
                            <input
                              type="number"
                              id="modulo"
                              value={selectedPoint ? selectedPoint.modulo : config.modulo}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Defaults para futuros pontos
                                handleInputChange('modulo', value);
                                // Grupo atual
                                if (selectedPoint && onUpdateGroupConfig) {
                                  onUpdateGroupConfig(selectedPoint.groupId, { modulo: String(value || '1') });
                                }
                              }}
                              min="1"
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor="idx">Saída (1-{moduleInfo.maxIdx})</label>
                            <input
                              type="number"
                              id="idx"
                              value={selectedPoint ? selectedPoint.idx : config.idx}
                              onChange={(e) => {
                                const value = e.target.value;
                                handleInputChange('idx', value);
                                if (selectedPoint && onUpdateGroupConfig) {
                                  onUpdateGroupConfig(selectedPoint.groupId, { idx: String(value || '1') });
                                }
                              }}
                              min="1"
                              max={moduleInfo.maxIdx}
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="alert alert-info">
                        Selecione um ponto para editar o circuito.
                      </div>
                    )}
                  </section>

                  {/* ========== CARD 3: LÂMPADA (INSTÂNCIA) ========== */}
                  <section className="panel-card panel-card--instance">
                    <div className="panel-card-header">
                      <span className="panel-card-title">Ponto de Luz Atual</span>
                      <span className="panel-chip">Instância</span>
                    </div>

                    {selectedPoint ? (
                      <>
                        {/* Grupo da lâmpada */}
                        <div className="form-group">
                          <label htmlFor="circleNumber">Número do grupo</label>
                          <div className="number-controls">
                            <button 
                              type="button"
                              className="number-btn decrease"
                              onClick={() => {
                                const next = Math.max(1, (currentCircleNumber || 1) - 1);
                                if (onUpdateInstanceConfig) {
                                  onUpdateInstanceConfig(selectedPoint.id, { circleNumber: next });
                                }
                              }}
                              disabled={currentCircleNumber <= 1}
                            >
                              −
                            </button>
                            <input
                              type="number"
                              id="circleNumber"
                              value={currentCircleNumber}
                              onChange={(e) => {
                                const raw = e.target.value;
                                const newNumber = Math.max(1, parseInt(raw, 10) || 1);
                                if (onUpdateInstanceConfig) {
                                  onUpdateInstanceConfig(selectedPoint.id, { circleNumber: newNumber });
                                }
                              }}
                              min="1"
                            />
                            <button 
                              type="button"
                              className="number-btn increase"
                              onClick={() => {
                                const next = (currentCircleNumber || 1) + 1;
                                if (onUpdateInstanceConfig) {
                                  onUpdateInstanceConfig(selectedPoint.id, { circleNumber: next });
                                }
                              }}
                            >
                              +
                            </button>
                          </div>
                        </div>

                        {/* Dimensões da instância */}
                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor="width">Largura (%)</label>
                            <input
                              type="number"
                              id="width"
                              value={currentWidth}
                              onChange={(e) => {
                                const value = parseFloat(e.target.value) || 0.1;
                                if (onUpdateInstanceConfig) {
                                  onUpdateInstanceConfig(selectedPoint.id, { width: value });
                                }
                              }}
                              step="0.1"
                              min="0.1"
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor="height">Altura (%)</label>
                            <input
                              type="number"
                              id="height"
                              value={currentHeight}
                              onChange={(e) => {
                                const value = parseFloat(e.target.value) || 0.1;
                                if (onUpdateInstanceConfig) {
                                  onUpdateInstanceConfig(selectedPoint.id, { height: value });
                                }
                              }}
                              step="0.1"
                              min="0.1"
                            />
                          </div>
                        </div>

                        {/* Posição no canvas (apenas light) */}
                        {selectedPoint?.tipo === 'light' && (
                          <div className="form-row">
                            <div className="form-group">
                              <label htmlFor="posX">Posição X (%)</label>
                              <input
                                type="number"
                                id="posX"
                                value={currentX}
                                onChange={(e) => {
                                  if (!onUpdateInstanceConfig) return;
                                  let value = parseFloat(e.target.value);
                                  if (Number.isNaN(value)) value = 0;
                                  value = Math.max(0, Math.min(100, value));

                                  onUpdateInstanceConfig(selectedPoint.id, {
                                    x: Number(value.toFixed(1))
                                  });
                                }}
                                step="0.1"
                                min="0"
                                max="100"
                              />
                            </div>
                            <div className="form-group">
                              <label htmlFor="posY">Posição Y (%)</label>
                              <input
                                type="number"
                                id="posY"
                                value={currentY}
                                onChange={(e) => {
                                  if (!onUpdateInstanceConfig) return;
                                  let value = parseFloat(e.target.value);
                                  if (Number.isNaN(value)) value = 0;
                                  value = Math.max(0, Math.min(100, value));

                                  onUpdateInstanceConfig(selectedPoint.id, {
                                    y: Number(value.toFixed(1))
                                  });
                                }}
                                step="0.1"
                                min="0"
                                max="100"
                              />
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="alert alert-info">
                        Selecione um ponto para editar a lâmpada.
                      </div>
                    )}
                  </section>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ===== FITAS LED ===== */}
        <div className="accordion-item">
          <button
            type="button"
            className="accordion-header"
            onClick={() => setIsLedOpen(open => !open)}
          >
            <div className="accordion-header-main">
              <span>🌈 Fitas LED</span>
              {config.lightType === 'ledStrip' && (
                <span className="accordion-badge">Ferramenta ativa</span>
              )}
            </div>
            <span className="accordion-icon">
              {isLedOpen ? '▾' : '▸'}
            </span>
          </button>

          {isLedOpen && (
            <div className="accordion-body">
              <div className="led-config">
                <h4>Configuração da Fita LED</h4>

                <div className="form-group">
                  <label htmlFor="ledStrokeWidth">Espessura (px)</label>
                  <input
                    type="number"
                    id="ledStrokeWidth"
                    value={config.ledStrokeWidth}
                    onChange={(e) =>
                      handleInputChange('ledStrokeWidth', parseFloat(e.target.value || 0))
                    }
                    step="0.5"
                    min="0.5"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="ledColor">Cor da Fita</label>
                  <div className="color-input-group">
                    <input
                      type="color"
                      id="ledColor"
                      value={config.ledColor}
                      onChange={(e) => handleInputChange('ledColor', e.target.value)}
                    />
                    <input
                      type="text"
                      value={config.ledColor}
                      onChange={(e) => handleInputChange('ledColor', e.target.value)}
                      placeholder="#6f42c1"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Info da Fita LED selecionada */}
      {selectedPoint && selectedPoint.tipo === 'led' && (
        <div className="selected-led-info">
          <h4>🌈 Fita LED selecionada</h4>
          <div className="led-details">
            <div className="detail-row">
              <span>Cor:</span>
              <span style={{ color: selectedPoint.ledColor }}>{selectedPoint.ledColor}</span>
            </div>
            <div className="detail-row">
              <span>Modo:</span>
              <span>{selectedPoint.ledMode}</span>
            </div>
            <div className="detail-row">
              <span>Espessura:</span>
              <span>{selectedPoint.ledStrokeWidth}px</span>
            </div>
          </div>
        </div>
      )}

      {/* Ações */}
      <div className="actions-section">
        <button 
          type="button"
          className="btn btn-clear"
          onClick={onClearAll}
          disabled={lightPoints.length === 0}
        >
          🗑️ Limpar Tudo
        </button>
      </div>
    </div>
  );
};

export default ControlPanel;